import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { CloseRounded as CloseRoundedIcon } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import {
  DrawerBody,
  DrawerButton,
  DrawerHeader,
  IconButton,
  Pagination,
  RadioLabel,
  Typography,
} from '../../design-system';
import { i18n } from '../../i18n';
import { notifications, NotificationsDrawerBase } from './NotificationsComponents';
import { NotificationsView } from './NotificationsView';
import { useNotificationsQuery, usePaginationState } from '../../hooks';
import { useStore } from '../../context';
import { TABLE } from '../../const';

const NotificationsDrawerBody = styled(DrawerBody)`
  & > *:nth-child(1) {
    margin-top: 1.6rem !important;
  }
  & > *:nth-child(n + 2) {
    margin-top: 1.6rem;
  }

  & > button {
    background-color: var(--gray-90);

    &:hover {
      background-color: var(--gray-90);
    }
  }
`;

const NotificationsDrawerIsRead = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
`;

export const NotificationsDrawer = ({
  isOpen,
  handleChangeDrawer,
}: {
  isOpen: boolean;
  handleChangeDrawer: () => void;
}) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const { page, take, skip, handlePageChange } = usePaginationState('notifications');
  const [isRead, setIsRead] = useState(false);
  const [openNotificationIndex, setOpenNotificationIndex] = useState<number | null>(null);

  const {
    data = { data: [], totalCount: 0 },
    isFetching,
    refetch,
  } = useNotificationsQuery({
    user,
    take,
    skip,
    isRead,
  });

  useEffect(() => {
    if (isOpen) return;

    setTimeout(() => setOpenNotificationIndex(null), 300);
  }, [isOpen]);

  const handleCloseNotification = (isRefetch = false) => {
    setOpenNotificationIndex(null);

    if (isRefetch) refetch();
  };

  if (openNotificationIndex !== null && data.data[openNotificationIndex]) {
    return (
      <NotificationsView
        isOpen={isOpen}
        onClose={handleCloseNotification}
        {...data.data[openNotificationIndex]}
      />
    );
  }

  return (
    <NotificationsDrawerBase isOpen={isOpen}>
      <DrawerHeader>
        <Typography variant={'textL'} weight={'bold'}>
          {i18n.t('pages.notifications.title')}
        </Typography>
        <IconButton onClick={handleChangeDrawer} iconColor={'--gray-70'}>
          <CloseRoundedIcon />
        </IconButton>
      </DrawerHeader>
      <NotificationsDrawerBody>
        <NotificationsDrawerIsRead>
          <RadioLabel
            checked={!isRead}
            onClick={() => setIsRead(false)}
            name={'notifications-is-read'}
            label={i18n.t('label.unread')}
          />
          <RadioLabel
            checked={isRead}
            onClick={() => setIsRead(true)}
            name={'notifications-is-read'}
            label={i18n.t('label.read')}
          />
        </NotificationsDrawerIsRead>
        {isFetching && (
          <DrawerButton
            isOpen={true}
            startIcon={<CircularProgress size={24} style={{ color: 'var(--white)' }} />}
          >
            {i18n.t('label.loading')}
          </DrawerButton>
        )}
        {!isFetching &&
          data.data.map((item, i) => (
            <DrawerButton
              key={item.createdAt}
              isOpen={true}
              startIcon={notifications[item.category].icon}
              onClick={() => setOpenNotificationIndex(i)}
            >
              {notifications[item.category].title}
            </DrawerButton>
          ))}
      </NotificationsDrawerBody>
      <Pagination
        count={Math.ceil(data.totalCount / TABLE.PAGE_MAX_SIZE)}
        page={page}
        siblingCount={1}
        onChange={handlePageChange}
      />
    </NotificationsDrawerBase>
  );
};
